// Archivo que maneja la configuración de los widgets

import React, { useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import {
  DEFAULT_WORKFLOW_WIDGET_CATALOG,
  readWorkflowWidgetCatalog,
  resetWorkflowWidgetCatalog,
  saveWorkflowWidgetCatalog,
} from './workflowWidgetCatalog'

const PREVIEW_STAGE_BY_MODE = {
  progress_stage: {
    number: 3,
    label: 'Validación documental',
    metric: 'SLA: 1.2 días',
  },
  billable_stage: {
    number: 5,
    label: 'Aprobación para facturación',
    metric: 'Monto listo para cobro: $45,000',
  },
}

const getFirstOptionValue = (options, fallback) => {
  if (Array.isArray(options) && options.length > 0 && options[0]?.value) {
    return options[0].value
  }
  return fallback
}

const withDefaultPresetDescription = (presets) =>
  presets.map((preset) => ({
    ...preset,
    description:
      preset?.description ||
      'Configuración recomendada para mostrar la etapa con lenguaje claro para operación.',
  }))

export default function WorkflowWidgetsConfig() {
  const [workflowWidgetCatalog, setWorkflowWidgetCatalog] = useState(
    readWorkflowWidgetCatalog
  )
  const [catalogSaved, setCatalogSaved] = useState('')

  const stageBannerCatalog =
    workflowWidgetCatalog?.workflow_stage_banner ||
    DEFAULT_WORKFLOW_WIDGET_CATALOG.workflow_stage_banner

  const metadata =
    stageBannerCatalog?.metadata ||
    DEFAULT_WORKFLOW_WIDGET_CATALOG.workflow_stage_banner.metadata

  const presetOptions =
    Array.isArray(stageBannerCatalog?.presets) &&
    stageBannerCatalog.presets.length > 0
      ? withDefaultPresetDescription(stageBannerCatalog.presets)
      : withDefaultPresetDescription(
          DEFAULT_WORKFLOW_WIDGET_CATALOG.workflow_stage_banner.presets
        )

  const resolverOptions =
    Array.isArray(stageBannerCatalog?.resolverKeys) &&
    stageBannerCatalog.resolverKeys.length > 0
      ? stageBannerCatalog.resolverKeys
      : DEFAULT_WORKFLOW_WIDGET_CATALOG.workflow_stage_banner.resolverKeys

  const modeOptions =
    Array.isArray(stageBannerCatalog?.modes) &&
    stageBannerCatalog.modes.length > 0
      ? stageBannerCatalog.modes
      : DEFAULT_WORKFLOW_WIDGET_CATALOG.workflow_stage_banner.modes

  const prefixOptions =
    Array.isArray(stageBannerCatalog?.prefixOptions) &&
    stageBannerCatalog.prefixOptions.length > 0
      ? stageBannerCatalog.prefixOptions
      : DEFAULT_WORKFLOW_WIDGET_CATALOG.workflow_stage_banner.prefixOptions

  const shapeOptions =
    Array.isArray(stageBannerCatalog?.shapeOptions) &&
    stageBannerCatalog.shapeOptions.length > 0
      ? stageBannerCatalog.shapeOptions
      : DEFAULT_WORKFLOW_WIDGET_CATALOG.workflow_stage_banner.shapeOptions

  const emptyStateOptions =
    Array.isArray(stageBannerCatalog?.emptyStateModes) &&
    stageBannerCatalog.emptyStateModes.length > 0
      ? stageBannerCatalog.emptyStateModes
      : DEFAULT_WORKFLOW_WIDGET_CATALOG.workflow_stage_banner.emptyStateModes

  const initialPresetId =
    stageBannerCatalog?.defaultPreset ||
    getFirstOptionValue(presetOptions, 'process_default')

  const [selectedPresetId, setSelectedPresetId] = useState(initialPresetId)

  const activePreset = useMemo(() => {
    const byId = presetOptions.find(
      (preset) => preset.value === selectedPresetId
    )
    if (byId) return byId
    return presetOptions[0] || null
  }, [presetOptions, selectedPresetId])

  const activeConfig = useMemo(() => {
    const presetConfig = activePreset?.config || {}
    return {
      resolverKey:
        presetConfig.resolverKey ||
        getFirstOptionValue(resolverOptions, 'workflow.current_stage'),
      mode:
        presetConfig.mode || getFirstOptionValue(modeOptions, 'progress_stage'),
      prefix:
        presetConfig.prefix || getFirstOptionValue(prefixOptions, 'ETAPA'),
      shape: presetConfig.shape || getFirstOptionValue(shapeOptions, 'rounded'),
      customLabel: presetConfig.customLabel || '',
      showStageNumber: presetConfig.showStageNumber !== false,
      showMetrics: presetConfig.showMetrics !== false,
      showQuotes: Boolean(presetConfig.showQuotes),
      emptyStateMode:
        presetConfig.emptyStateMode ||
        getFirstOptionValue(emptyStateOptions, 'message'),
      emptyMessage:
        presetConfig.emptyMessage || 'Sin información de etapa disponible',
    }
  }, [
    activePreset,
    resolverOptions,
    modeOptions,
    prefixOptions,
    shapeOptions,
    emptyStateOptions,
  ])

  const updatePresetConfig = (key, value) => {
    setWorkflowWidgetCatalog((prev) => {
      const prevBanner =
        prev?.workflow_stage_banner ||
        DEFAULT_WORKFLOW_WIDGET_CATALOG.workflow_stage_banner
      const prevPresets = Array.isArray(prevBanner.presets)
        ? prevBanner.presets
        : []

      const nextPresets = prevPresets.map((preset) =>
        preset.value === (activePreset?.value || selectedPresetId)
          ? {
              ...preset,
              config: {
                ...(preset.config || {}),
                [key]: value,
              },
            }
          : preset
      )

      return {
        ...prev,
        workflow_stage_banner: {
          ...prevBanner,
          presets: nextPresets,
        },
      }
    })
    setCatalogSaved('')
  }

  const setDefaultPreset = (nextPresetId) => {
    setSelectedPresetId(nextPresetId)
    setWorkflowWidgetCatalog((prev) => ({
      ...prev,
      workflow_stage_banner: {
        ...(prev?.workflow_stage_banner || {}),
        defaultPreset: nextPresetId,
      },
    }))
    setCatalogSaved('')
  }

  const previewStage =
    PREVIEW_STAGE_BY_MODE[activeConfig.mode] || PREVIEW_STAGE_BY_MODE.progress_stage

  const previewTitle = activeConfig.customLabel?.trim()
    ? activeConfig.customLabel.trim()
    : activeConfig.prefix

  const previewStageText = activeConfig.showStageNumber
    ? `${previewTitle} ${previewStage.number}`
    : previewTitle

  const previewDescription = activeConfig.showQuotes
    ? `“${previewStage.label}”`
    : previewStage.label

  const isEmptyBehaviorMessage = activeConfig.emptyStateMode === 'message'

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Box>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Configuración de widgets de workflow
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 0.75 }}>
          Define cómo se comunica el estado del proceso al equipo operativo
          mediante configuraciones guiadas y reutilizables.
        </Typography>
      </Box>

      <Card variant="outlined">
        <CardContent>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Widget disponible
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 0.5 }}>
                {metadata?.name || 'Banner de etapa'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                <strong>Propósito:</strong>{' '}
                {metadata?.purpose ||
                  'Comunicar de forma clara la etapa actual del proceso.'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                {metadata?.description}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <Chip color="success" label={metadata?.status || 'Disponible'} />
              <Chip
                variant="outlined"
                label={metadata?.audience || 'Operación'}
              />
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Grid container spacing={2}>
        <Grid item xs={12} lg={7}>
          <Stack spacing={2}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  Presets reutilizables
                </Typography>
                <TextField
                  select
                  fullWidth
                  label="Preset activo"
                  value={activePreset?.value || ''}
                  onChange={(event) => setSelectedPresetId(event.target.value)}
                >
                  {presetOptions.map((preset) => (
                    <MenuItem key={preset.value} value={preset.value}>
                      {preset.label}
                    </MenuItem>
                  ))}
                </TextField>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  {activePreset?.description}
                </Typography>
                <Box sx={{ mt: 1.5 }}>
                  <Button
                    variant="outlined"
                    onClick={() => setDefaultPreset(activePreset?.value || '')}
                    disabled={!activePreset?.value}
                  >
                    Marcar como preset predeterminado
                  </Button>
                </Box>
              </CardContent>
            </Card>

            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  1) Qué deseas mostrar
                </Typography>
                <Stack spacing={1.25}>
                  <TextField
                    select
                    fullWidth
                    label="Fuente de etapa"
                    value={activeConfig.resolverKey}
                    onChange={(event) =>
                      updatePresetConfig('resolverKey', event.target.value)
                    }
                  >
                    {resolverOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    fullWidth
                    label="Enfoque del seguimiento"
                    value={activeConfig.mode}
                    onChange={(event) =>
                      updatePresetConfig('mode', event.target.value)
                    }
                  >
                    {modeOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    fullWidth
                    label="Nombre visible del banner (opcional)"
                    value={activeConfig.customLabel}
                    onChange={(event) =>
                      updatePresetConfig('customLabel', event.target.value)
                    }
                  />
                </Stack>
              </CardContent>
            </Card>

            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  2) Cómo debe verse
                </Typography>
                <Stack spacing={1.25}>
                  <TextField
                    select
                    fullWidth
                    label="Texto inicial"
                    value={activeConfig.prefix}
                    onChange={(event) =>
                      updatePresetConfig('prefix', event.target.value)
                    }
                  >
                    {prefixOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    fullWidth
                    label="Estilo visual"
                    value={activeConfig.shape}
                    onChange={(event) =>
                      updatePresetConfig('shape', event.target.value)
                    }
                  >
                    {shapeOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
              </CardContent>
            </Card>

            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  3) Qué detalles adicionales deseas mostrar
                </Typography>
                <Stack>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={activeConfig.showStageNumber}
                        onChange={(event) =>
                          updatePresetConfig(
                            'showStageNumber',
                            event.target.checked
                          )
                        }
                      />
                    }
                    label="Mostrar número de etapa"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={activeConfig.showMetrics}
                        onChange={(event) =>
                          updatePresetConfig(
                            'showMetrics',
                            event.target.checked
                          )
                        }
                      />
                    }
                    label="Mostrar indicador adicional (métrica)"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={activeConfig.showQuotes}
                        onChange={(event) =>
                          updatePresetConfig('showQuotes', event.target.checked)
                        }
                      />
                    }
                    label="Resaltar el nombre de etapa entre comillas"
                  />
                </Stack>
              </CardContent>
            </Card>

            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  4) Qué hacer si no hay información
                </Typography>
                <Stack spacing={1.25}>
                  <TextField
                    select
                    fullWidth
                    label="Comportamiento sin etapa"
                    value={activeConfig.emptyStateMode}
                    onChange={(event) =>
                      updatePresetConfig('emptyStateMode', event.target.value)
                    }
                  >
                    {emptyStateOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    fullWidth
                    label="Mensaje cuando no hay etapa"
                    value={activeConfig.emptyMessage}
                    onChange={(event) =>
                      updatePresetConfig('emptyMessage', event.target.value)
                    }
                    disabled={!isEmptyBehaviorMessage}
                  />
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Stack spacing={2}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  5) Vista previa
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1.5 }}
                >
                  Simulación visual del banner para validar configuración antes
                  de guardar.
                </Typography>

                <Box
                  sx={{
                    border: '1px solid',
                    borderColor:
                      activeConfig.mode === 'billable_stage'
                        ? 'warning.main'
                        : 'divider',
                    borderLeftWidth: activeConfig.shape === 'chevron' ? 6 : 1,
                    borderRadius: activeConfig.shape === 'flat' ? 0.5 : 2,
                    px: 2,
                    py: 1.5,
                    backgroundColor:
                      activeConfig.mode === 'billable_stage'
                        ? 'warning.50'
                        : 'background.default',
                  }}
                >
                  <Stack spacing={0.75}>
                    <Typography variant="overline" sx={{ letterSpacing: 1 }}>
                      {previewStageText}
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                      {previewDescription}
                    </Typography>
                    {activeConfig.showMetrics ? (
                      <Typography variant="caption" color="text.secondary">
                        {previewStage.metric}
                      </Typography>
                    ) : null}
                  </Stack>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, mb: 0.75 }}
                >
                  Simulación cuando no hay etapa
                </Typography>
                {activeConfig.emptyStateMode === 'hide' ? (
                  <Alert severity="info">
                    El banner no se mostrará cuando no exista etapa activa.
                  </Alert>
                ) : (
                  <Alert severity="warning">
                    {activeConfig.emptyMessage ||
                      'Sin información de etapa disponible'}
                  </Alert>
                )}
              </CardContent>
            </Card>

            <Card variant="outlined">
              <CardContent>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 700, mb: 1.25 }}
                >
                  Acciones de catálogo
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <Button
                    variant="contained"
                    onClick={() => {
                      saveWorkflowWidgetCatalog(workflowWidgetCatalog)
                      setCatalogSaved(
                        'Catálogo guardado localmente en este navegador.'
                      )
                    }}
                  >
                    Guardar configuración
                  </Button>
                  <Button
                    variant="outlined"
                    color="inherit"
                    onClick={() => {
                      resetWorkflowWidgetCatalog()
                      setWorkflowWidgetCatalog(DEFAULT_WORKFLOW_WIDGET_CATALOG)
                      setSelectedPresetId(
                        DEFAULT_WORKFLOW_WIDGET_CATALOG.workflow_stage_banner
                          .defaultPreset
                      )
                      setCatalogSaved(
                        'Catálogo restablecido a valores por defecto.'
                      )
                    }}
                  >
                    Restablecer catálogo
                  </Button>
                </Stack>
                {catalogSaved ? (
                  <Alert severity="success" sx={{ mt: 1.25 }}>
                    {catalogSaved}
                  </Alert>
                ) : null}
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  )
}
